import { Fragment, type ReactNode } from 'react'
import { Text } from '@/ui'
import { mtc, TypographyStyles } from '@/styles'
import { CARD_RADIUS } from './PlanCardParts'
import { formatReceiptDate } from './lib/helpers'

export const RECEIPT_SHELL = `${CARD_RADIUS} border border-neutral-200 overflow-hidden`

export interface ReceiptHeaderRowProps {
  createdAt: string
}

export const ReceiptHeaderRow = ({ createdAt }: ReceiptHeaderRowProps) => (
  <div className="flex items-center justify-end p-[22px]">
    <Text
      variant={TypographyStyles.BODY_SM}
      className="text-neutral-500 hidden md:block"
    >
      {formatReceiptDate(createdAt)}
    </Text>
  </div>
)

export type ReceiptCalloutTone = 'moss' | 'yellow'

const CALLOUT_TONE_CLASSNAME: Record<ReceiptCalloutTone, string> = {
  moss: 'bg-moss-0 border-moss-50',
  yellow: 'bg-yellow-0 border-yellow-50',
}

const CALLOUT_SUBLINE_COLOR: Record<ReceiptCalloutTone, string> = {
  moss: 'text-moss-200',
  yellow: 'text-neutral-600',
}

/** The scheduled callout leads with the date it bills, so its headline carries the weight. */
const CALLOUT_HEADLINE_WEIGHT: Record<ReceiptCalloutTone, string> = {
  moss: '',
  yellow: 'font-medium',
}

export interface ReceiptCalloutProps {
  tone: ReceiptCalloutTone
  headline: string
  subline?: string
}

export const ReceiptCallout = ({
  tone,
  headline,
  subline,
}: ReceiptCalloutProps) => (
  <div
    data-testid="receipt-callout"
    className={mtc(
      'flex flex-col gap-1 rounded-2xl border p-5',
      CALLOUT_TONE_CLASSNAME[tone]
    )}
  >
    <Text
      variant={TypographyStyles.BODY_LG}
      className={CALLOUT_HEADLINE_WEIGHT[tone]}
    >
      {headline}
    </Text>
    {subline && (
      <Text
        variant={TypographyStyles.BODY_SM}
        className={CALLOUT_SUBLINE_COLOR[tone]}
      >
        {subline}
      </Text>
    )}
  </div>
)

export interface ReceiptRow {
  testId: string
  label: string
  value: string
}

export const ReceiptRowGroup = ({ rows }: { rows: ReceiptRow[] }) => (
  <div className="flex flex-col gap-[14px]">
    {rows.map((row, index) => (
      <Fragment key={row.testId}>
        {index > 0 && <div className="h-px bg-neutral-100" />}
        <ReceiptSingleRow row={row} />
      </Fragment>
    ))}
  </div>
)

const ReceiptSingleRow = ({ row }: { row: ReceiptRow }) => (
  <div data-testid={row.testId} className="flex items-center justify-between">
    <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
      {row.label}
    </Text>
    <Text variant={TypographyStyles.LABEL_MD} className="text-neutral-900">
      {row.value}
    </Text>
  </div>
)

export const ReceiptCardBody = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col gap-4 px-[22px] pb-[22px]">{children}</div>
)
